import React, { useRef, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import SavedPlaces from './SavedPlaces';
import Columns from './columns/Columns';
import { startScrolling, stopScrolling } from '../../utils/ScrollUtils';
import { FaCaretRight, FaCaretLeft, FaSave, FaEdit, FaClone, FaMap, FaTrash } from "react-icons/fa";
import { usePlaceContext } from '../itinerary/PlaceContext';
import debounce from 'lodash/debounce';
import '../../styles/components/itinerary/ItineraryContent.css';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import Popup from '../common/popup/Popup';
import ToggleSwitch from '../../components/common/ToggleSwitch';
import { useAuth } from '../../AuthContext';
import useApi from '../../Api';
import Tooltip from '@mui/material/Tooltip';

const ItineraryContent = ({
  selectedItinerary,
  columns,
  onCreateNew,
  onRenameItinerary,
  onDeleteItinerary,
  onUpdatePrivacy,
  isCreatingItinerary,
  isRenamingItinerary,
  isDeletingItinerary,
  isUpdatingPrivacy,
  toggleMap,
  isSmallScreen,
  showMap,
  readOnly
}) => {
  const scrollRef = useRef(null);
  const leftScrollBarRef = useRef(null);
  const rightScrollBarRef = useRef(null);
  const { saveItinerary, state: placeState } = usePlaceContext();
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const { origin_id } = useParams();
  const [isUpdatingPrivacyLocal, setIsUpdatingPrivacyLocal] = useState(false);
  const { token, user } = useAuth();
  const apiClient = useApi();
  const [duplicating, setDuplicating] = useState(false);
  const [publicState, setPublicState] = useState(selectedItinerary?.is_public ?? false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (selectedItinerary) {
      setPublicState(selectedItinerary.is_public ?? false);
    }
  }, [selectedItinerary]);

  const checkScroll = useCallback(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
    const maxScrollLeft = scrollWidth - clientWidth;
    if (leftScrollBarRef.current) {
      leftScrollBarRef.current.style.display = scrollLeft > 0 ? 'block' : 'none';
    }
    if (rightScrollBarRef.current) {
      rightScrollBarRef.current.style.display = scrollLeft < maxScrollLeft ? 'block' : 'none';
    }
  }, []);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScroll);
      checkScroll();
    }
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkScroll);
      }
    };
  }, [checkScroll]);

  const handleSaveClick = useCallback(
    debounce(async () => {
      if (!selectedItinerary) return;
      setIsSaving(true);
      try {
        const result = await saveItinerary();
        if (result.success) {
          toast.success('Itinerary saved successfully!', {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else {
          toast.error('Failed to save the itinerary. Please try again.', {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      } catch (error) {
        toast.error('An unexpected error occurred while saving. Please try again.', {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        console.error('Save Itinerary Error:', error);
      } finally {
        setIsSaving(false);
      }
    }, 500),
    [saveItinerary, selectedItinerary]
  );

  const handleEditClick = () => {
    if (selectedItinerary) {
      navigate(`/itinerary/${origin_id}/edit/${selectedItinerary.id}`);
    }
  };

  const handleDuplicateClick = async () => {
    if (!selectedItinerary) return;
    setDuplicating(true);
    try {
      const response = await apiClient.post(`/itineraries/${selectedItinerary.id}/duplicate`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newItineraryId = response.data.new_itinerary_id;
      navigate(`/itinerary/${origin_id}/edit/${newItineraryId}`);
    } catch (error) {
      console.error("Failed to duplicate itinerary:", error);
    } finally {
      setDuplicating(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    onDeleteItinerary(selectedItinerary.id);
    setShowDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleToggleChange = (newValue) => {
    if (newValue) {
      const savedPlaces = placeState.savedPlaces[selectedItinerary.id] || [];
      const nonCustomInSaved = savedPlaces.find(place => !place.custom_place_id);
      const nonCustomInColumns = columns.some(column => column.items && column.items.find(place => !place.custom_place_id));
      if (nonCustomInSaved || nonCustomInColumns) {
        toast.error("Please remove non-custom places to make public", {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return;
      }
    }
    setIsUpdatingPrivacyLocal(true);
    onUpdatePrivacy(selectedItinerary.id, newValue)
      .then(() => {
        setPublicState(newValue);
        toast.success(newValue ? 'Itinerary is now public!' : 'Itinerary set to private!', {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      })
      .catch(err => {
        console.error("Failed to update privacy:", err);
        if (err.response && err.response.data && err.response.data.detail === "Itinerary contains non-custom places, cannot be set to public") {
          toast.error("Please remove non-custom places to make public", {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else {
          toast.error('Failed to update privacy.', {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      })
      .finally(() => {
        setIsUpdatingPrivacyLocal(false);
      });
  };

  const toggleDisabled = selectedItinerary
    ? (() => {
        const savedPlaces = placeState.savedPlaces[selectedItinerary.id] || [];
        const nonCustomInSaved = savedPlaces.some(place => !place.custom_place_id);
        const nonCustomInColumns = columns.some(column => column.items && column.items.some(place => !place.custom_place_id));
        return nonCustomInSaved || nonCustomInColumns;
      })()
    : false;

  return (
    <section className="itinerary-content">
      {selectedItinerary ? (
        <div className="itinerary-box">
          <SavedPlaces selectedItinerary={selectedItinerary} readOnly={readOnly} />
          <div className="itinerary-columns-wrapper">
            <div
              className="itinerary-scroll-bar itinerary-left-scroll-bar"
              ref={leftScrollBarRef}
              onMouseEnter={() => startScrolling(scrollRef, 'left', 5, checkScroll)}
              onMouseLeave={stopScrolling}
              onDragEnter={() => startScrolling(scrollRef, 'left', 5, checkScroll)}
              onDragLeave={stopScrolling}
              style={{ display: 'none' }}
            >
              <FaCaretLeft alt="Expand Left Sidebar" className="itinerary-sidebar-icon-left" />
            </div>
            <Columns initialColumns={columns} selectedItinerary={selectedItinerary} ref={scrollRef} onColumnsChange={checkScroll} readOnly={readOnly} />
            <div
              className="itinerary-scroll-bar itinerary-right-scroll-bar"
              ref={rightScrollBarRef}
              onMouseEnter={() => startScrolling(scrollRef, 'right', 5, checkScroll)}
              onMouseLeave={stopScrolling}
              onDragEnter={() => startScrolling(scrollRef, 'right', 5, checkScroll)}
              onDragLeave={stopScrolling}
              style={{ display: 'none' }}
            >
              <FaCaretRight alt="Expand Right Sidebar" className="itinerary-sidebar-icon-right" />
            </div>
          </div>
          <div className="itinerary-save-button-container">
            {isSmallScreen && !showMap && (
              <button className="itinerary-preview-button" onClick={toggleMap} title="Show Map">
                <FaMap className="itinerary-edit-icon" />
                Show Map
              </button>
            )}
            {!readOnly && (
              <>
                <button className={`itinerary-save-button ${isSaving ? 'saving' : ''}`} onClick={handleSaveClick} disabled={isSaving} title="Save Itinerary">
                  {isSaving ? <span className="itinerary-spinner"></span> : (<><FaSave className="itinerary-save-icon" /> Save Itinerary</>)}
                </button>
                <button className="itinerary-delete-button" onClick={handleDeleteClick} title="Delete Itinerary">
                  <FaTrash className="itinerary-save-icon" />
                  Delete Itinerary
                </button>
                <div className="itinerary-public-toggle-container">
                  <Tooltip title={toggleDisabled ? "Please remove non-custom places to make public" : ""} arrow>
                    <span>
                      <ToggleSwitch
                        id="privacy-toggle"
                        checked={publicState}
                        onChange={handleToggleChange}
                        disabled={toggleDisabled}
                        loading={isUpdatingPrivacyLocal}
                        confirmTitle="Make Public"
                        confirmMessage="Making this itinerary public will display it for everyone. Do you want to proceed?"
                      />
                    </span>
                  </Tooltip>
                </div>
              </>
            )}
            {readOnly && (
              <>
                {!user?.isPremium ? (
                  <Tooltip title="Requires Premium subscription to use this feature" arrow enterTouchDelay={0} leaveTouchDelay={3000}>
                    <span>
                      <button className="itinerary-save-button" onClick={handleDuplicateClick} title="Create Copy of Itinerary" disabled={duplicating || !user?.isPremium}>
                        {duplicating ? <span className="itinerary-spinner"></span> : (<><FaClone className="itinerary-save-icon" /> Create Copy</>)}
                      </button>
                    </span>
                  </Tooltip>
                ) : (
                  <button className="itinerary-save-button" onClick={handleDuplicateClick} title="Create Copy of Itinerary" disabled={duplicating}>
                    {duplicating ? <span className="itinerary-spinner"></span> : (<><FaClone className="itinerary-save-icon" /> Create Copy</>)}
                  </button>
                )}
                {selectedItinerary?.is_owner && (
                  !user?.isPremium ? (
                    <Tooltip title="Requires Premium subscription to use this feature" arrow enterTouchDelay={0} leaveTouchDelay={3000}>
                      <span>
                        <button className="itinerary-save-button" onClick={handleEditClick} title="Edit Itinerary" disabled={!user?.isPremium}>
                          <FaEdit className="itinerary-save-icon" /> Edit Itinerary
                        </button>
                      </span>
                    </Tooltip>
                  ) : (
                    <button className="itinerary-save-button" onClick={handleEditClick} title="Edit Itinerary">
                      <FaEdit className="itinerary-save-icon" /> Edit Itinerary
                    </button>
                  )
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="itinerary-no-message">Please select or create an itinerary to save places.</div>
      )}
      {showDeleteConfirm && (
        <Popup
          title="Confirm Deletion"
          message={selectedItinerary && selectedItinerary.id ? 'Are you sure you want to delete this guide? This action cannot be undone.' : 'Are you sure you want to discard this unsaved guide? This action cannot be undone.'}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </section>
  );
};

ItineraryContent.propTypes = {
  selectedItinerary: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    columns: PropTypes.array,
    tags: PropTypes.array,
    is_public: PropTypes.bool
  }),
  columns: PropTypes.array.isRequired,
  onCreateNew: PropTypes.func.isRequired,
  onRenameItinerary: PropTypes.func.isRequired,
  onDeleteItinerary: PropTypes.func.isRequired,
  onUpdatePrivacy: PropTypes.func.isRequired,
  isCreatingItinerary: PropTypes.bool.isRequired,
  isRenamingItinerary: PropTypes.bool.isRequired,
  isDeletingItinerary: PropTypes.bool.isRequired,
  isUpdatingPrivacy: PropTypes.bool.isRequired,
  toggleMap: PropTypes.func.isRequired,
  isSmallScreen: PropTypes.bool.isRequired,
  showMap: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
};

export default ItineraryContent;
